/**
=========================================================
* Custom Sidenav Component - DoorSharp Dashboard (module-style)
=========================================================
*/

import React, { useState, useEffect, useMemo } from "react";
import { useLocation, NavLink, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

// MSAL imports
import { useMsal } from "@azure/msal-react";

// @mui material components
import { Box, IconButton, Tooltip, Collapse, useMediaQuery } from "@mui/material";
import Icon from "@mui/material/Icon";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 3 PRO React context
import { useMaterialUIController, setMiniSidenav } from "context";
import logo from "assets/images/doorsharp-logo.png"; // Import logo

import DashboardIcon from '@mui/icons-material/Dashboard';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
// import SpeakerNotesIcon from '@mui/icons-material/SpeakerNotes';
import SpeakerNotesIcon from '@mui/icons-material/ViewListOutlined';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import BuildIcon from '@mui/icons-material/Build';
import Network from "assets/sidebaricons/network.svg";
import Insights from "assets/sidebaricons/insights.svg";
import LegalcyReports from "assets/sidebaricons/legalcyreport.svg";


function CustomSidenav({ routes, onMouseEnter, onMouseLeave, setIsHovercheckExpanded, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav } = controller;
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname } = location;
  const isPhone = useMediaQuery('(max-width:599px)'); // Only phones get overlay behavior
  const isMobile = isPhone; // Tablets and above get push behavior

  // MSAL hook for logout
  const { instance } = useMsal();

  const [lastActiveRoute, setLastActiveRoute] = useState(pathname);
  const [activeModule, setActiveModule] = useState("Analysis");
  const [openDashboards, setOpenDashboards] = useState(true);
  const [openNetwork, setOpenNetwork] = useState(false);
  const [openInsights, setOpenInsights] = useState(false);
  const [openLegacyReports, setOpenLegacyReports] = useState(false);
  const [openAdmin, setOpenAdmin] = useState(false);
  const [openUserProfile, setOpenUserProfile] = useState(false);
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    if (pathname !== lastActiveRoute) setLastActiveRoute(pathname);
  }, [pathname]);

  // Load user info from localStorage
  useEffect(() => {
    const loadUserInfo = () => {
      try {
        const storedUserInfo = localStorage.getItem('userInfo');
        if (storedUserInfo) {
          setUserInfo(JSON.parse(storedUserInfo));
        }
      } catch (error) {
        console.error('Error loading user info:', error);
      }
    };

    loadUserInfo();

    // Listen for storage changes to update user info
    const handleStorageChange = (e) => {
      if (e.key === 'userInfo') {
        loadUserInfo();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Logout handler function - Local logout only (doesn't sign out from other Microsoft apps)
  const handleLogout = async () => {
    try {
      // Clear application-specific tokens from localStorage
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("userInfo");

      // Clear MSAL cache from sessionStorage (app-specific only)
      // MSAL stores cache in sessionStorage with keys prefixed by clientId
      if (instance) {
        try {
          instance.setActiveAccount(null);

          // Get all accounts and clear them from MSAL cache
          const accounts = instance.getAllAccounts();
          if (accounts.length > 0) {
            // Clear MSAL cache entries for this app only
            // MSAL stores cache in sessionStorage with specific keys
            const clientId = instance.getConfiguration()?.auth?.clientId;
            if (clientId) {
              // Clear MSAL cache entries for this specific clientId
              Object.keys(sessionStorage).forEach(key => {
                if (key.includes(clientId) || key.startsWith('msal.')) {
                  sessionStorage.removeItem(key);
                }
              });
            }
          }
        } catch (msalError) {
          console.warn("Error clearing MSAL cache:", msalError);
          // Fallback: clear all sessionStorage if MSAL-specific clearing fails
          // This is safe since we're only clearing on logout
          sessionStorage.clear();
        }
      } else {
        // If no MSAL instance, just clear sessionStorage
        sessionStorage.clear();
      }

      // Use window.location.href to force a full page reload
      // This ensures ProtectedRoute and MSAL hooks re-evaluate authentication state
      window.location.href = '/authentication/sign-in/basic';
    } catch (error) {
      console.error("Logout error:", error);
      // Fallback: ensure cleanup and redirect with full reload
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("userInfo");
      sessionStorage.clear();
      window.location.href = '/authentication/sign-in/basic';
    }
  };

  const [openMiniMenu, setOpenMiniMenu] = useState(null);

  const menus = useMemo(
    () => ({
      Analysis: {
        header: "ANALYTICS",
        items: [
          {
            name: "Dashboards",
            icon: <DashboardIcon sx={{ width: 20, height: 20 }} />,
            children: [
              { name: "My Dashboard", abbrev: "MD", route: "/dashboards/my-dashboard" },
              { name: "Financials", abbrev: "FI", route: "/dashboards/financials" },
              { name: "Commercial", abbrev: "CO", route: "/dashboards/commercial" },
              { name: "Operations", abbrev: "OP", route: "/dashboards/operations" },
              { name: "Technology", abbrev: "TE", route: "/dashboards/technology" },
              { name: "Marketing", abbrev: "MA", route: "/dashboards/marketing" },
              { name: "Human Resources", abbrev: "HR", route: "/dashboards/human-resources" },
            ],
          },
          {
            name: "Intelligence",
            icon: <QuestionAnswerIcon sx={{ width: 20, height: 20 }} />,
            route: "/intelligence"
          },
          {
            name: "Network",
            icon: (
              <Box
                component="img"
                src={Network}
                alt="Network"
                sx={{ width: 20, height: 20 }}
              />
            ),
            children: [
              { name: "Retailers", abbrev: "RE", route: "/network/retailers" },
              { name: "Distributors", abbrev: "DI", route: "/network/distributors" },
              {
                name: "Store Locations", abbrev: "SL",
                route: "/network/store-locations"
              },
              { name: "Warehouses", abbrev: "WA", route: "/network/warehouses" },
              { name: "Sales Representatives", abbrev: "SP", route: "/network/sales-representatives" },
            ],
          },
          {
            name: "Insights",
            icon: (
              <Box
                component="img"
                src={Insights}
                alt="Insights"
                sx={{ width: 20, height: 20 }}
              />
            ),
            children: [
              { name: "Leadership", abbrev: "LR", route: "/insights/leadership" },
              { name: "Commercial", abbrev: "CO", route: "/insights/commercial" },
              { name: "Distribution", abbrev: "DI", route: "/insights/distribution" },
              { name: "Finance", abbrev: "FI", route: "/insights/finance" },
            ],
          },
          // {
          //   name: "Legacy Reports",
          //   icon: <FindInPageIcon fontSize="small" />,
          //   children: [
          //     { name: "LaunchPad", abbrev: "LP", route: "/legacy-reports/launchpad" },
          //   ],
          // },
          {
            name: "Legacy Reports",
            icon: (
              <Box
                component="img"
                src={LegalcyReports}
                alt="Legacy Reports"
                sx={{ width: 20, height: 20 }}
              />
            ),
            children: [
              {
                name: "LaunchPad",
                abbrev: "LP",
                externalUrl: "https://app.powerbi.com/groups/me/reports/95bd4812-bb42-440d-a29f-443a0a8a007c/2ddf0cb6f93cd3b8a6c0?experience=power-bi",
                target: "_blank"
              }
            ],
          },

          {
            name: "Admin/Setup",
            icon: <BuildIcon sx={{ width: 20, height: 20 }} />,
            children: [],
          },
        ],
      }
    }),
    []
  );

  const currentMenu = menus[activeModule];

  // Handle closing sidenav when clicking overlay on mobile
  const handleOverlayClick = () => {
    if (isMobile) {
      setMiniSidenav(dispatch, true);
    }
  };

  const [isHoverExpanded, setIsHoverExpanded] = useState(false);

  useEffect(() => {
    if (!miniSidenav || isMobile || isHoverExpanded) return;

    const activeParent = currentMenu.items.find(item =>
      item.children?.some(child => child.route === pathname)
    );

    if (activeParent) {
      setOpenMiniMenu(activeParent.name);
    }
  }, [
    pathname,
    miniSidenav,
    isMobile,
    isHoverExpanded,
    currentMenu.items,
  ]);

  const NON_CLICKABLE_CHILDREN = [
    "Store Locations",
    "Warehouses",
    "Sales Representatives",
    "Leadership",
    "Distribution",
    "Finance",
  ];

  const clearHoverState = () => {
    setIsHoverExpanded(false);
    setIsHovercheckExpanded(false);
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isMobile && !miniSidenav && (
        <Box
          onClick={handleOverlayClick}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 999,
            transition: "opacity 0.25s ease",
          }}
        />
      )}
      <Box
        className="custom-sidenav"
        onMouseEnter={() => {
          if (!isMobile && miniSidenav) {
            setIsHoverExpanded(true);          // sidebar UI
            setIsHovercheckExpanded(true);     // header + page margin
          }
        }}
        onMouseLeave={() => {
          if (!isMobile && miniSidenav) {
            setIsHoverExpanded(false);
            clearHoverState();

          }
        }}
        sx={{
          position: "fixed !important",
          left: "0 !important",
          top: "0 !important",
          width: isMobile
            ? "260px"
            : miniSidenav
              ? isHoverExpanded
                ? "260px"
                : "68px"
              : "260px",
          height: "100vh",
          maxHeight: "100vh",
          backgroundColor: "#0e0e0f",
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          padding: "0px 0",
          zIndex: 5001,
          transition: "transform 0.25s ease, width 0.25s ease",
          transform: isMobile && miniSidenav ? "translateX(-100%)" : "translateX(0)",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >

        {/* Module Switch + Label */}
        <Box
          sx={{
            padding: "16px 12px 0px 16px", // gap from left & right
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: miniSidenav ? "space-between" : "space-between",
              borderBottom: "1px solid #FFFFFF40",
              paddingBottom: miniSidenav ? "12px" : "16px", // space above border
            }}
          >
            {/* Logo + Header */}
            <MDBox
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "22px",
                cursor: "default",
              }}
            >
              <MDBox
                component="img"
                src={logo}
                alt="DoorSharp Analysis"
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                }}
              />

              {(!miniSidenav || isHoverExpanded) && (
                <MDTypography
                  variant="button"
                  sx={{
                    color: "#FFFFFF",
                    letterSpacing: 1,
                    whiteSpace: "nowrap",
                    fontWeight: 400,
                    fontSize: "16px"
                  }}
                >
                  {currentMenu.header}
                </MDTypography>
              )}
            </MDBox>

            {/* LEFT icon – full sidebar */}
            {!miniSidenav && !isHoverExpanded && (
              <Icon

                onClick={() => {
                  clearHoverState();
                  setMiniSidenav(dispatch, true)
                }}
                sx={{ color: "#FFFFFFBF", cursor: "pointer" }}
              >
                chevron_left
              </Icon>
            )}

            {/* RIGHT icon – hover expanded */}
            {miniSidenav && isHoverExpanded && (
              <Icon
                onClick={() => {
                  clearHoverState();
                  setMiniSidenav(dispatch, false); // lock open
                  setIsHoverExpanded(false);
                }}
                sx={{ color: "#FFFFFFBF", cursor: "pointer" }}
              >
                chevron_right
              </Icon>
            )}

          </Box>
        </Box>



        {/* User Profile Section */}
        {(
          <Box
            sx={{
              padding: "16px 12px 0px 18px", // ← creates 12px left/right gap
              cursor: "pointer",
              "&:hover": { backgroundColor: "#151518" },
            }}
            onClick={() => setOpenUserProfile(!openUserProfile)}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: miniSidenav ? "center" : "space-between",
                gap: "22px",
                borderBottom: "1px solid #FFFFFF40",
                paddingBottom: miniSidenav ? "12px" : "16px", // spacing above border
              }}
            >
              <MDBox
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor: "#3a3a3d",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon sx={{ color: "#FFFFFFBF", fontSize: "small" }}>person</Icon>
              </MDBox>

              {/* Username – hidden in mini */}
              {(!miniSidenav || isHoverExpanded) && (
                <MDTypography
                  variant="button"
                  sx={{ color: "#FFFFFFBF", flex: 1, fontSize: "13px", fontWeight: 300 }}
                >
                  {userInfo?.name || userInfo?.email?.split("@")[0] || "User"}
                </MDTypography>
              )}

              {/* Expand icon – hidden in mini */}
              {!miniSidenav && (
                <Icon sx={{ color: "#FFFFFFBF", fontSize: "small" }}>
                  expand_more
                </Icon>
              )}
            </Box>
          </Box>
        )}


        {/* Navigation Items */}

        <Box sx={{ display: "flex", flexDirection: "column", width: "100%", gap: "0px" }}>

          {currentMenu.items.map((item, index) => {
            const isActive = item.route && pathname === item.route;
            const hasChildren = Array.isArray(item.children) && item.children.length > 0;
            const open = miniSidenav
              ? openMiniMenu === item.name
              : item.name === "Dashboards" ? openDashboards
                : item.name === "Network" ? openNetwork
                  : item.name === "Insights" ? openInsights
                    : item.name === "Legacy Reports" ? openLegacyReports
                      : item.name === "Admin/Setup" ? openAdmin
                        : false;

            return (
              <Box key={`${item.name}-${index}`}>
                <Box
                  component={item.route ? NavLink : "div"}
                  to={item.route || "#"}
                  onClick={() => {
                    if (!isMobile && miniSidenav && hasChildren) {
                      setOpenMiniMenu(prev =>
                        prev === item.name ? null : item.name
                      );
                      return;
                    }
                    if (item.name === "Dashboards") setOpenDashboards((v) => !v);
                    if (item.name === "Network") setOpenNetwork((v) => !v);
                    if (item.name === "Insights") setOpenInsights((v) => !v);
                    if (item.name === "Legacy Reports") setOpenLegacyReports((v) => !v);
                    if (item.name === "Admin/Setup") setOpenAdmin((v) => !v);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: (isMobile || !miniSidenav) ? "12px" : "12px",
                    justifyContent: (isMobile || !miniSidenav) ? "flex-start" : "flex-start",
                    padding: "12px 24px",
                    color: isActive ? "#ffffff" : "#FFFFFFBF",
                    textDecoration: "none",
                    cursor: "pointer",
                    backgroundColor: isActive ? "#1f1f24" : "transparent",
                    "&:hover": { backgroundColor: "#151518" },
                    fontWeight: "300 !important",
                    marginTop: index === 0 ? "12px" : "8px"
                  }}
                >
                  <Icon fontSize="small" sx={{ color: "#FFFFFFBF", fontWeight: 300 }}>{item.icon}</Icon>
                  {(isMobile || !miniSidenav || isHoverExpanded) && (
                    <MDTypography variant="button" sx={{ color: "#FFFFFFBF", fontWeight: 300, fontSize: "13px" }}>
                      {item.name}
                    </MDTypography>
                  )}
                  {hasChildren && (isMobile || !miniSidenav || isHoverExpanded) && (
                    <Icon sx={{ marginLeft: "auto", color: "#FFFFFFBF", fontWeight: 300, fontSize: "13px" }}>{open ? "expand_less" : "expand_more"}</Icon>
                  )}
                </Box>
                {hasChildren && (
                  <>
                    {(isMobile || !miniSidenav || isHoverExpanded) && (
                      <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ display: "flex", flexDirection: "column", padding: "4px 0 8px 0" }}>
                          {item.children.map((child) => {
                            const childActive = pathname === child.route;
                            const isExternal = !!child.externalUrl;
                            const isChildDisabled = NON_CLICKABLE_CHILDREN.includes(child.name);

                            return (
                              <Box
                                key={child.route || child.externalUrl}
                                component={
                                  isChildDisabled
                                    ? "div"
                                    : isExternal
                                      ? "a"
                                      : NavLink
                                }
                                {...(!isChildDisabled &&
                                  (isExternal
                                    ? {
                                      href: child.externalUrl,
                                      target: "_blank",
                                      rel: "noopener noreferrer",
                                    }
                                    : { to: child.route }))}
                                onClick={
                                  !isChildDisabled && isMobile
                                    ? () => setMiniSidenav(dispatch, true)
                                    : undefined
                                }
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "12px",
                                  padding: "12px 12px 12px 12px",
                                  color: isChildDisabled
                                    ? "#6f7277 !important"
                                    : childActive
                                      ? "#ffffff"
                                      : "#9aa0a6",

                                  background:
                                    childActive && !isChildDisabled
                                      ? "linear-gradient(0deg, #1672B9, #1672B9), linear-gradient(90deg, #1672B9 0%, #115F9B 100%)"
                                      : "none",
                                  borderRadius: "4px",
                                  textDecoration: "none",
                                  boxShadow: childActive && !isChildDisabled ? "0px 2px 24px 0px rgba(22, 114, 185, 0.5)" : "none",
                                  cursor: isChildDisabled ? "default" : "pointer",
                                  pointerEvents: isChildDisabled ? "none" : "auto",

                                  "&:hover": !isChildDisabled
                                    ? { backgroundColor: "#FFFFFF1A" }
                                    : {},
                                  marginRight: "12px",
                                  marginLeft: "12px"
                                }}
                              >
                                {child.abbrev && (
                                  <MDTypography
                                    variant="caption"
                                    sx={{
                                      color: isChildDisabled
                                        ? "#6f7277"
                                        : childActive
                                          ? "#FFFFFF"
                                          : "#FFFFFF80", width: "24px", fontSize: "13px", fontWeight: 100
                                    }}
                                  >
                                    {child.abbrev}
                                  </MDTypography>
                                )
                                }

                                <MDTypography variant="button" sx={{
                                  color: isChildDisabled
                                    ? "#6f7277"
                                    : childActive
                                      ? "#FFFFFF"
                                      : "#FFFFFFBF", flex: 1, fontSize: "13px", fontWeight: 200,
                                }}>
                                  {child.name}
                                </MDTypography>

                                {
                                  child.product_count !== undefined && (
                                    <MDTypography
                                      variant="caption"
                                      sx={{ color: "#7f8287", fontSize: "0.7rem" }}
                                    >
                                      {child.product_count}
                                    </MDTypography>
                                  )
                                }
                              </Box>
                            );
                          })}
                        </Box>
                      </Collapse>

                    )}
                    {!isMobile && miniSidenav && hasChildren && openMiniMenu === item.name && !isHoverExpanded && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          // padding: "4px 0 8px 0",
                          alignItems: "center",
                          marginRight: "12px",
                          marginLeft: "12px"
                        }}
                      >
                        {item.children.map((child) => {
                          const childActive = pathname === child.route;
                          const isExternal = !!child.externalUrl;
                          const isChildDisabled = NON_CLICKABLE_CHILDREN.includes(child.name);

                          return (
                            <Box
                              key={child.route || child.externalUrl}
                              component={
                                isChildDisabled
                                  ? "div"
                                  : isExternal
                                    ? "a"
                                    : NavLink
                              }
                              {...(!isChildDisabled &&
                                (isExternal
                                  ? {
                                    href: child.externalUrl,
                                    target: "_blank",
                                    rel: "noopener noreferrer",
                                  }
                                  : { to: child.route }))}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "12px 16px",
                                width: "100%",
                                borderRadius: "4px",
                                textDecoration: "none",
                                height: "40px",

                                color: isChildDisabled
                                  ? "#6f7277"
                                  : childActive
                                    ? "#ffffff"
                                    : "#9aa0a6",

                                background:
                                  childActive && !isChildDisabled
                                    ? "linear-gradient(0deg, #1672B9, #1672B9), linear-gradient(90deg, #1672B9 0%, #115F9B 100%)"
                                    : "transparent",

                                cursor: isChildDisabled ? "default" : "pointer",
                                pointerEvents: isChildDisabled ? "none" : "auto",
                                boxShadow: childActive && !isChildDisabled ? "0px 2px 24px 0px rgba(22, 114, 185, 0.5)" : "none",
                                "&:hover": !isChildDisabled
                                  ? {
                                    background:
                                      "#FFFFFF1A",
                                  }
                                  : {},
                                marginRight: "12px",
                                marginLeft: "12px"
                              }}
                            >
                              <MDTypography
                                variant="caption"
                                sx={{
                                  color: isChildDisabled
                                    ? "#6f7277"
                                    : childActive
                                      ? "#FFFFFF"
                                      : "#FFFFFF80", width: "24px", fontSize: "13px", fontWeight: 100,
                                  fontSize: "13px",
                                  fontWeight: 100,
                                  marginRight: "12px",
                                  marginLeft: "12px"
                                }}
                              >
                                {child.abbrev}
                              </MDTypography>
                            </Box>
                          );
                        })}
                      </Box>
                    )}


                  </>
                )
                }
              </Box>
            );
          })}
        </Box>

        {/* Logout */}
        <Box sx={{ borderTop: "1px solid rgba(255,255,255,0.08)", padding: "8px 8px 0 8px", marginTop: "auto" }}>
          <Box
            onClick={handleLogout}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: (isMobile || !miniSidenav) ? "12px" : "12px",
              justifyContent: (isMobile || !miniSidenav) ? "flex-start" : "flex-start",
              padding: "10px 16px",
              color: "#b0b3b8",
              textDecoration: "none",
              cursor: "pointer",
              "&:hover": { backgroundColor: "#151518" },
              borderRadius: "8px",
            }}
          >
            <Icon fontSize="small">logout</Icon>
            {(isMobile || !miniSidenav || isHoverExpanded) && (
              <MDTypography variant="button" sx={{ color: "inherit" }}>Logout</MDTypography>
            )}
          </Box>
        </Box>
      </Box >
    </>
  );
}

CustomSidenav.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
};

export default CustomSidenav;
